<script>
import { h } from "vue";
import { classes, formattedValue, getValue, normalizeFields } from "./table";

export default {
    name: "SbTableLite",
    props: {
        fields: { type: Array, default: () => [] },
        items: { type: Array, default: () => [] },
        busy: { type: Boolean, default: false },
        sortBy: { type: String, default: null },
        sortDesc: { type: Boolean, default: false },
        striped: { type: Boolean, default: false },
        hover: { type: Boolean, default: false },
        small: { type: Boolean, default: false },
        responsive: { type: [Boolean, String], default: false },
        primaryKey: { type: String, default: null },
        theadClass: { type: [String, Array, Object], default: null },
        tbodyTrClass: { type: [String, Array, Object, Function], default: null },
    },
    data() { return { details: new Set(), providerBusy: false }; },
    computed: {
        displayItems() { return this.items; },
        normalizedFields() { return normalizeFields(this.fields, this.displayItems); },
        effectiveBusy() { return this.busy || Boolean(this.providerBusy); },
        activeSortBy() { return this.sortBy; },
        activeSortDesc() { return Boolean(this.sortDesc); },
        tableClasses() { return ["table", "b-table", this.striped && "table-striped", this.hover && !this.effectiveBusy && "table-hover", this.small && "table-sm"]; },
    },
    methods: {
        rowKey(item, index) { return this.primaryKey && item[this.primaryKey] !== undefined ? item[this.primaryKey] : index; },
        isDetailsShowing(item, index) { return Boolean(item._showDetails) || this.details.has(this.rowKey(item, index)); },
        toggleDetails(item, index) {
            if (Object.prototype.hasOwnProperty.call(item, "_showDetails")) {
                item._showDetails = !item._showDetails;
                return;
            }
            const key = this.rowKey(item, index);
            this.details.has(key) ? this.details.delete(key) : this.details.add(key);
            this.details = new Set(this.details);
        },
        onHeaderClick() {},
        renderHead() {
            return h("thead", { class: this.theadClass }, [h("tr", this.normalizedFields.map((field, column) => {
                const slot = this.$slots[`head(${field.key})`] || this.$slots["head()"];
                return h("th", {
                    scope: "col",
                    role: "columnheader",
                    "aria-colindex": column + 1,
                    "aria-sort": field.sortable ? (this.activeSortBy === field.key ? (this.activeSortDesc ? "descending" : "ascending") : "none") : null,
                    class: [field.thClass, field.sortable && "b-table-sortable-column", this.activeSortBy === field.key && "b-table-sorted"],
                    onClick: () => this.onHeaderClick(field),
                }, slot ? slot({ label: field.label, column: field.key, field }) : String(field.label ?? ""));
            }))]);
        },
        renderBusy() {
            const slot = this.$slots["table-busy"];
            if (!this.effectiveBusy || !slot) { return null; }
            return h("tr", { class: "b-table-busy-slot" }, [h("td", { colspan: this.normalizedFields.length }, slot())]);
        },
        renderRows() {
            const rows = [];
            const busy = this.renderBusy();
            if (busy) { rows.push(busy); }
            this.displayItems.forEach((item, index) => {
                const key = this.rowKey(item, index);
                const detailsShowing = this.isDetailsShowing(item, index);
                rows.push(h("tr", { key: `row-${key}`, class: classes(this.tbodyTrClass, item) }, this.normalizedFields.map((field, column) => {
                    const unformatted = getValue(item, field.key);
                    const value = formattedValue(item, field);
                    const scope = { item, index, field, value, unformatted, detailsShowing, toggleDetails: () => this.toggleDetails(item, index) };
                    const slot = this.$slots[`cell(${field.key})`] || this.$slots["cell()"];
                    return h("td", { class: field.tdClass, role: "cell", "aria-colindex": column + 1 }, slot ? slot(scope) : (value == null ? "" : String(value)));
                })));
                const detailsSlot = this.$slots["row-details"];
                if (detailsShowing && detailsSlot) {
                    const scope = { item, index, fields: this.normalizedFields, detailsShowing, toggleDetails: () => this.toggleDetails(item, index) };
                    rows.push(h("tr", { key: `details-${key}`, class: "b-table-details" }, [h("td", { colspan: this.normalizedFields.length }, detailsSlot(scope))]));
                }
            });
            return rows;
        },
        renderTable() {
            return h("table", { class: this.tableClasses, role: "table", "aria-busy": String(this.effectiveBusy), "aria-colcount": this.normalizedFields.length }, [this.renderHead(), h("tbody", this.renderRows())]);
        },
    },
    render() {
        const table = this.renderTable();
        return this.responsive ? h("div", { class: this.responsive === true ? "table-responsive" : `table-responsive-${this.responsive}` }, [table]) : table;
    },
};
</script>
